import { useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { Card } from 'antd'
import { faCalendarStar, faGraduationCap, faLinkSimple, faMerge, faSave } from '@fortawesome/pro-solid-svg-icons'
import { isFunction } from 'lodash'

import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import IconLabel from '../../../Components/icons/IconLabel'
import IconButton from '../../../Components/antd/Buttons/IconButton'
import FormItem from '../../../Components/shared/Forms/FormItem'
import SearchList from '../../../Components/antd/Lists/SearchList'
import SectionsSelector from '../../../Components/shared/Selectors/SectionsSelector'
import { getFullName } from '../../../utils/entities/sectionToYear'
import { normalizeName } from '../../../utils/string'
import { getIdOrNull, isObject, validateString } from '../../../utils'

const RequestSectionValidator = ({ disabled = false, request, schoolSections = [], onMerge, onSkip, onValidate }) => {
  const t = useSelector(selectTranslate)

  const [selectedSchoolSection, setSelectedSchoolSection] = useState(null)
  const [selectedSection, setSelectedSection] = useState(null)

  const pickedSectionToYear = isObject(request.sectionToYear) ? request.sectionToYear : null
  const sectionName = request.sectionName ?? ''
  const schoolYearName = request.schoolYearName ?? ''
  const hasSection = pickedSectionToYear !== null || validateString(sectionName, { notEmpty: true }).valid

  const sectionToLink = getIdOrNull(pickedSectionToYear?.section)

  useEffect(() => {
    setSelectedSection(sectionToLink)
  }, [sectionToLink])

  const duplicate = useMemo(() => {
    if (pickedSectionToYear !== null) {
      return schoolSections.find(ss => getIdOrNull(ss.sectionToYear) === getIdOrNull(pickedSectionToYear)) ?? null
    }

    const name = normalizeName(sectionName)
    const year = normalizeName(schoolYearName)

    if (name === '') {
      return null
    }

    return schoolSections.find(ss => {
      const sty = ss.sectionToYear
      const matchesSection = normalizeName(sty.sectionName) === name || normalizeName(sty.sectionAbbreviation) === name

      return matchesSection && (year === '' || normalizeName(sty.schoolYearName) === year)
    }) ?? null
  }, [pickedSectionToYear, sectionName, schoolYearName, schoolSections])

  useEffect(() => {
    setSelectedSchoolSection(duplicate?.id ?? null)
  }, [duplicate?.id])

  const isSectionLinked = useMemo(
    () => selectedSection !== null && schoolSections.some(ss => getIdOrNull(ss.sectionToYear?.section) === selectedSection),
    [schoolSections, selectedSection]
  )

  const options = useMemo(() => [...schoolSections]
    .sort((a, b) => {
      if (duplicate !== null && (a.id === duplicate.id || b.id === duplicate.id)) {
        return a.id === duplicate.id ? -1 : 1
      }

      return getFullName(a.sectionToYear).localeCompare(getFullName(b.sectionToYear))
    })
    .map(ss => ({ label: getFullName(ss.sectionToYear), value: ss.id, tooltip: getFullName(ss.sectionToYear) })),
  [schoolSections, duplicate])

  return (
    <div className='h-100 w-100 flex-row'>
      <div className='flex-column h-100 w-50 mr-8 jc-between'>
        <h3>{t('request_section_validator.title')}</h3>
        <Card size='small' title={t('common.section')}>
          <IconLabel
            icon={faGraduationCap}
            text={pickedSectionToYear !== null
              ? pickedSectionToYear.sectionName
              : (hasSection ? sectionName : t('request_section_validator.no_section_proposed'))}
            fontSize='small'
          />
          {(pickedSectionToYear !== null || schoolYearName !== '') && (
            <IconLabel
              icon={faCalendarStar}
              text={pickedSectionToYear?.schoolYearName ?? schoolYearName}
              fontSize='small'
            />
          )}
        </Card>
        {hasSection && (
          <div className='mt-12'>
            <FormItem icon={faGraduationCap} label='request_section_validator.section_to_link' className='form-select'>
              <SectionsSelector multiple='default' values={selectedSection} onChange={setSelectedSection} />
            </FormItem>
            <IconButton
              className='mt-8'
              disabled={disabled || !selectedSection || duplicate !== null || isSectionLinked}
              icon={faSave}
              text={t('request_section_validator.validate_button')}
              type='primary'
              onClick={() => onValidate(selectedSection)}
            />
          </div>
        )}
      </div>
      <div className='flex-column h-100 w-50'>
        <h3>{t('request_section_validator.description')}</h3>
        <SearchList data={options} selected={selectedSchoolSection} onSelectedChange={setSelectedSchoolSection} />
        <IconButton
          className='mt-12'
          disabled={disabled || !selectedSchoolSection}
          icon={faMerge}
          text={t('request_section_validator.merge_button')}
          type='primary'
          onClick={() => onMerge(schoolSections.find(ss => ss.id === selectedSchoolSection))}
        />
        {!hasSection && isFunction(onSkip) && (
          /** Skipping a section the request points at would throw on SCHOOL_SECTION_MUST_BE_CREATED at conversion. */
          <IconButton
            className='mt-8'
            disabled={disabled}
            icon={faLinkSimple}
            text={t('request_section_validator.skip_button')}
            onClick={onSkip}
          />
        )}
      </div>
    </div>
  )
}

export default RequestSectionValidator
